import React, { useState, useEffect, useRef } from 'react';
import { SR15_SPM1_DATA } from '../../data/sr15Data';
import { SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Bell, Play, Pause, RotateCcw, AlertTriangle } from 'lucide-react';

interface SR15SPM1WarmingProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SR15SPM1Warming: React.FC<SR15SPM1WarmingProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['sr15_spm1'];
  const audioEngine = AudioEngine.getInstance();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Auto-play timeline loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % SR15_SPM1_DATA.length);
      }, 700);
    } else {
      audioEngine.stopNatWarmth();
      audioEngine.stopAnthropoPulse();
    }
    return () => clearInterval(timer);
  }, [isPlaying]);

  // Audio Update per step
  useEffect(() => {
    if (!audioEngine.getIsUnlocked() || !isPlaying) return;
    const pt = SR15_SPM1_DATA[currentIndex];
    if (!pt) return;

    const temp = pt.observedGMST !== null ? pt.observedGMST : pt.humanForcedGMST;
    const freq = audioEngine.tempToFreq(temp);

    // If reaching 1.5°C (2040), trigger warning chime
    if (pt.year === 2040 || temp >= 1.5) {
      audioEngine.playWarningChime(392.00); // G4 chime
    }

    audioEngine.startAnthropoPulse(freq, 4 + temp * 3, 0.35);
  }, [currentIndex, isPlaying]);

  // Canvas Drawing Effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = (canvas.width = canvas.parentElement?.clientWidth || 800);
    const height = (canvas.height = 320);

    ctx.clearRect(0, 0, width, height);

    const padX = 50;
    const padY = 40;
    const chartW = width - padX * 2;
    const chartH = height - padY * 2;

    const minYear = 1850;
    const maxYear = 2050;
    const minTemp = -0.4;
    const maxTemp = 2.0;

    const getX = (yr: number) => padX + ((yr - minYear) / (maxYear - minYear)) * chartW;
    const getY = (t: number) => padY + chartH - ((t - minTemp) / (maxTemp - minTemp)) * chartH;

    // Grid lines & Zero Baseline
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)';
    ctx.lineWidth = 1;
    for (let t = 0.0; t <= 1.8; t += 0.5) {
      const y = getY(t);
      ctx.beginPath();
      ctx.moveTo(padX, y);
      ctx.lineTo(padX + chartW, y);
      ctx.stroke();

      ctx.fillStyle = '#64748b';
      ctx.font = '10px monospace';
      ctx.fillText(`${t > 0 ? '+' : ''}${t.toFixed(1)}°C`, 10, y + 3);
    }

    // 1.5°C Target Threshold Line (Red Dashed)
    const line15Y = getY(1.5);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    ctx.moveTo(padX, line15Y);
    ctx.lineTo(padX + chartW, line15Y);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#ef4444';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText('1.5°C Global Warming Threshold', padX + 10, line15Y - 6);

    // Draw Historical GMST Line (Cyan)
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    let started = false;
    SR15_SPM1_DATA.forEach((pt) => {
      if (pt.observedGMST !== null) {
        const x = getX(pt.year);
        const y = getY(pt.observedGMST);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
    });
    ctx.stroke();

    // Draw Projected Trend Line (Amber Dashed)
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    started = false;
    SR15_SPM1_DATA.forEach((pt) => {
      if (pt.projectedTrend !== null) {
        const x = getX(pt.year);
        const y = getY(pt.projectedTrend);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
    });
    ctx.stroke();
    ctx.setLineDash([]);

    // Active Scrubber Line & Glow
    const activePt = SR15_SPM1_DATA[currentIndex];
    if (activePt) {
      const scrubX = getX(activePt.year);
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 2;
      ctx.shadowColor = '#06b6d4';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(scrubX, padY);
      ctx.lineTo(scrubX, padY + chartH);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Active Node Dot
      const currentTemp = activePt.observedGMST !== null ? activePt.observedGMST : activePt.humanForcedGMST;
      const dotY = getY(currentTemp);
      ctx.fillStyle = activePt.year >= 2040 ? '#ef4444' : '#38bdf8';
      ctx.beginPath();
      ctx.arc(scrubX, dotY, 6, 0, Math.PI * 2);
      ctx.fill();

      // Year Text Anchor
      ctx.fillStyle = '#f8fafc';
      ctx.font = 'bold 12px monospace';
      ctx.fillText(`Year ${activePt.year} (+${currentTemp.toFixed(2)}°C)`, scrubX - 45, padY - 10);
    }
  }, [currentIndex]);

  const activePt = SR15_SPM1_DATA[currentIndex];
  const currentTemp = activePt.observedGMST !== null ? activePt.observedGMST : activePt.humanForcedGMST;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Timeline' : 'Play 1.5°C Threshold Pulse'}</span>
        </button>
      </div>

      {/* Main Interactive Canvas */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-4">
        <canvas ref={canvasRef} className="w-full h-80 rounded-xl bg-slate-950/80 cursor-pointer" />

        {/* Timeline Scrubber Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>1850 AD</span>
            <span className={`font-bold ${currentTemp >= 1.5 ? 'text-red-400 flex items-center gap-1' : 'text-cyan-300'}`}>
              Year {activePt.year} (+{currentTemp.toFixed(2)}°C)
              {currentTemp >= 1.5 && <AlertTriangle className="w-3.5 h-3.5 inline text-red-400" />}
            </span>
            <span>2050 AD</span>
          </div>
          <input
            type="range"
            min="0"
            max={SR15_SPM1_DATA.length - 1}
            value={currentIndex}
            onChange={(e) => setCurrentIndex(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
